// Payments Page - Mobile Top-up Services
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Search, Globe, Smartphone, ChevronRight, RefreshCw, Zap, Info } from "lucide-react";
import { SBER, API, pageVariants, tapAnimation } from "../utils/constants";
import { SberCard } from "../components/UIComponents";

const PaymentsPage = ({ user, rate, onBack, t }) => {
  const [step, setStep] = useState('categories');
  const [countries, setCountries] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [account, setAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [activeCategory, setActiveCategory] = useState('mobile');
  
  const userId = user?.telegram_id || user?.user_id;
  
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get(`${API}/services/countries`);
        setCountries(res.data.countries || []);
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    };
    fetchCountries();
  }, []);
  
  const fetchProviders = async (country) => {
    setLoadingProviders(true);
    try {
      const res = await axios.get(`${API}/services/providers?country=${encodeURIComponent(country)}`);
      setProviders(res.data.providers || []);
    } catch (err) {
      toast.error(t ? t('errors.loadingError') : 'Ошибка загрузки операторов');
    }
    setLoadingProviders(false);
  };
  
  const fetchProvidersByCategory = async (category) => {
    setLoadingProviders(true);
    try {
      const res = await axios.get(`${API}/services/providers?category=${category}`);
      setProviders(res.data.providers || []);
    } catch (err) {
      console.error('Error fetching providers:', err);
    }
    setLoadingProviders(false);
  };
  
  const handleSelectCountry = async (country) => {
    setSelectedCountry(country);
    await fetchProviders(country);
    setStep('providers');
  };
  
  const handleSelectProvider = (provider) => {
    setSelectedProvider(provider);
    setStep('payment');
  };
  
  const handlePayment = async () => {
    if (!account || account.length < 5) {
      toast.error(t ? t('auth.enterPhone') : 'Введите корректный номер');
      return;
    }
    
    const amountNum = parseFloat(amount) || 0;
    if (selectedProvider.min_amount > 0 && amountNum <= 0) {
      toast.error(t ? t('topup.enterAmount') : 'Введите сумму');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/services/pay`, {
        service_id: selectedProvider.id,
        account: account,
        amount: amountNum || selectedProvider.min_amount,
        telegram_id: user?.telegram_id,
        user_id: user?.user_id
      });
      
      if (res.data.success) {
        toast.success(res.data.message || (t ? t('payments.paymentSuccess') : 'Оплата успешна!'));
        setStep('categories');
        setSelectedProvider(null);
        setSelectedCountry(null);
        setAccount('');
        setAmount('');
      } else {
        toast.error(res.data.message || (t ? t('payments.paymentError') : 'Ошибка оплаты'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (t ? t('payments.paymentError') : 'Ошибка оплаты'));
    }
    setLoading(false);
  };
  
  const userBalanceUsdt = user?.balance_usd || user?.balance_usdt || 0;
  
  const countryFlags = {
    'Казахстан': '🇰🇿', 'Кыргызстан': '🇰🇬', 'Россия': '🇷🇺', 'Узбекистан': '🇺🇿',
    'Армения': '🇦🇲', 'Азербайджан': '🇦🇿', 'Таджикистан': '🇹🇯', 'Турция': '🇹🇷', 'США': '🇺🇸'
  };
  
  const filteredCountries = countries.filter(c => c.toLowerCase().includes(searchQuery.toLowerCase()));
  
  // Categories view
  if (step === 'categories') {
    return (
      <motion.div 
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="min-h-screen pb-6"
        style={{ background: SBER.bg }}
        data-testid="payments-page"
      >
        <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ background: SBER.white }}>
          <motion.button 
            whileTap={tapAnimation}
            onClick={onBack}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: SBER.grayPale }}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: SBER.gray }} />
          </motion.button>
          <h1 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>
            {t ? t('payments.title') : 'Оплата услуг'}
          </h1>
        </div>
        
        <div className="px-5 pt-5">
          <div className="flex gap-2 mb-5">
            <motion.button
              whileTap={tapAnimation}
              onClick={() => setActiveCategory('mobile')}
              className="flex-1 py-3 rounded-xl font-medium text-[14px]"
              style={{ 
                background: activeCategory === 'mobile' ? SBER.green : SBER.white,
                color: activeCategory === 'mobile' ? '#fff' : SBER.dark
              }}
              data-testid="tab-mobile"
            >
              📱 {t ? t('payments.mobileTopup') : 'Мобильная связь'}
            </motion.button>
            <motion.button
              whileTap={tapAnimation}
              onClick={() => { setActiveCategory('gaming'); fetchProvidersByCategory('gaming'); setStep('providers'); }}
              className="flex-1 py-3 rounded-xl font-medium text-[14px]"
              style={{ 
                background: activeCategory === 'gaming' ? SBER.green : SBER.white,
                color: activeCategory === 'gaming' ? '#fff' : SBER.dark
              }}
              data-testid="tab-gaming"
            >
              🎮 PlayStation
            </motion.button>
          </div>
          
          <SberCard className="p-3 mb-5">
            <div className="flex items-center gap-3">
              <Search className="w-5 h-5" style={{ color: SBER.grayLight }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t ? t('auth.searchCountry') : 'Поиск страны'}
                className="flex-1 bg-transparent outline-none text-[15px]"
                style={{ color: SBER.dark }}
                data-testid="payments-search"
              />
            </div>
          </SberCard>
          
          {!searchQuery && (
            <>
              <p className="text-[13px] font-semibold mb-3" style={{ color: SBER.gray }}>
                {t ? t('landing.frequentQuestions').split(' ')[0] : 'ПОПУЛЯРНЫЕ'}
              </p>
              <div className="grid grid-cols-4 gap-3 mb-6">
                {['Кыргызстан', 'Казахстан', 'Узбекистан'].filter(c => countries.includes(c)).map((country) => (
                  <motion.button
                    key={country}
                    whileTap={tapAnimation}
                    onClick={() => handleSelectCountry(country)}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl"
                    style={{ background: SBER.white }}
                    data-testid={`country-${country}`}
                  >
                    <span className="text-[28px]">{countryFlags[country] || '🌍'}</span>
                    <span className="text-[11px] font-medium text-center" style={{ color: SBER.dark }}>
                      {country}
                    </span>
                  </motion.button>
                ))}
              </div>
            </>
          )}
          
          <p className="text-[13px] font-semibold mb-3" style={{ color: SBER.gray }}>
            {searchQuery ? 'РЕЗУЛЬТАТЫ' : (t ? t('home.currencies') : 'ВСЕ СТРАНЫ')}
          </p>
          <div className="space-y-2">
            {filteredCountries.map((country) => (
              <SberCard key={country} className="p-4" onClick={() => handleSelectCountry(country)}>
                <div className="flex items-center gap-4">
                  <span className="text-[28px]">{countryFlags[country] || '🌍'}</span>
                  <div className="flex-1">
                    <p className="font-medium text-[15px]" style={{ color: SBER.dark }}>{country}</p>
                  </div>
                  <ChevronRight className="w-5 h-5" style={{ color: SBER.grayLight }} />
                </div>
              </SberCard>
            ))}
          </div>
          
          {filteredCountries.length === 0 && (
            <SberCard className="p-8 text-center">
              <Globe className="w-12 h-12 mx-auto mb-3" style={{ color: SBER.grayLight }} />
              <p className="font-medium" style={{ color: SBER.gray }}>{t ? t('history.noHistory') : 'Ничего не найдено'}</p>
            </SberCard>
          )}
        </div>
      </motion.div>
    );
  }
  
  // Providers list view
  if (step === 'providers') {
    return (
      <motion.div 
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="min-h-screen pb-6"
        style={{ background: SBER.bg }}
      >
        <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ background: SBER.white }}>
          <motion.button 
            whileTap={tapAnimation}
            onClick={() => { setStep('categories'); setActiveCategory('mobile'); }}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: SBER.grayPale }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: SBER.gray }} />
          </motion.button>
          <div className="flex items-center gap-2">
            {selectedCountry && <span className="text-[24px]">{countryFlags[selectedCountry] || '🌍'}</span>}
            <h1 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>
              {activeCategory === 'gaming' ? 'PlayStation Store' : selectedCountry}
            </h1>
          </div>
        </div>
        
        <div className="px-5 pt-5">
          {loadingProviders ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin" style={{ color: SBER.green }} />
            </div>
          ) : (
            <>
              <p className="text-[13px] font-semibold mb-3" style={{ color: SBER.gray }}>
                {activeCategory === 'gaming' ? 'ВЫБЕРИТЕ НОМИНАЛ' : (t ? t('payments.selectOperator') : 'ВЫБЕРИТЕ ОПЕРАТОРА')}
              </p>
              <div className="space-y-2">
                {providers.map((provider) => (
                  <SberCard 
                    key={provider.id} 
                    className="p-4" 
                    onClick={() => handleSelectProvider(provider)}
                    data-testid={`provider-${provider.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                        style={{ background: activeCategory === 'gaming' ? '#0070D1' : SBER.green }}
                      >
                        {activeCategory === 'gaming' ? '🎮' : provider.name[0]}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[15px]" style={{ color: SBER.dark }}>{provider.name}</p>
                        <p className="text-[13px]" style={{ color: SBER.grayLight }}>
                          {provider.min_amount > 0 
                            ? `${t ? t('topup.min') : 'от'} ${provider.min_amount} - ${provider.max_amount} ${provider.currency}`
                            : `Фиксированная цена`
                          }
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5" style={{ color: SBER.grayLight }} />
                    </div>
                  </SberCard>
                ))}
              </div>
              
              {providers.length === 0 && (
                <SberCard className="p-8 text-center">
                  <Smartphone className="w-12 h-12 mx-auto mb-3" style={{ color: SBER.grayLight }} />
                  <p className="font-medium" style={{ color: SBER.gray }}>Нет доступных операторов</p>
                </SberCard>
              )}
            </>
          )}
        </div>
      </motion.div>
    );
  }
  
  // Payment form view
  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-6"
      style={{ background: SBER.bg }}
    >
      <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ background: SBER.white }}>
        <motion.button 
          whileTap={tapAnimation}
          onClick={() => setStep('providers')}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: SBER.grayPale }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: SBER.gray }} />
        </motion.button>
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
            style={{ background: activeCategory === 'gaming' ? '#0070D1' : SBER.green }}
          >
            {activeCategory === 'gaming' ? '🎮' : selectedProvider?.name?.[0]}
          </div>
          <h1 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>
            {selectedProvider?.name}
          </h1>
        </div>
      </div>
      
      <div className="px-5 pt-5">
        <SberCard className="p-4 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-[13px]" style={{ color: SBER.gray }}>
              {t ? t('exchange.insufficientFunds').split(' ')[0] : 'Ваш баланс'}
            </span>
            <span className="font-semibold text-[15px]" style={{ color: SBER.green }}>
              {userBalanceUsdt.toFixed(2)} USD
            </span>
          </div>
        </SberCard>
        
        <SberCard className="p-5 mb-4">
          <label className="text-[13px] font-medium mb-3 block" style={{ color: SBER.green }}>
            {selectedProvider?.account_format === 'email' ? 'Email аккаунта PSN' : (t ? t('payments.phoneNumber') : 'Номер телефона')}
          </label>
          <input
            type={selectedProvider?.account_format === 'email' ? 'email' : 'tel'}
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            placeholder={selectedProvider?.account_mask || '+7 XXX XXX XX XX'}
            className="w-full text-[18px] font-semibold bg-transparent outline-none"
            style={{ color: SBER.dark }}
            data-testid="account-input"
          />
        </SberCard>
        
        {selectedProvider?.min_amount > 0 && (
          <SberCard className="p-5 mb-4">
            <label className="text-[13px] font-medium mb-3 block" style={{ color: SBER.green }}>
              {t ? t('withdraw.amount') : 'Сумма'} ({selectedProvider?.currency})
            </label>
            <div className="flex flex-wrap gap-2 mb-4">
              {[selectedProvider.min_amount, selectedProvider.min_amount * 2, selectedProvider.min_amount * 5, selectedProvider.min_amount * 10]
                .filter(a => a <= selectedProvider.max_amount)
                .map((amt) => (
                <motion.button
                  key={amt}
                  whileTap={tapAnimation}
                  onClick={() => setAmount(String(amt))}
                  className="px-4 py-3 rounded-xl text-[14px] font-semibold transition-all"
                  style={{ 
                    background: amount === String(amt) ? SBER.green : SBER.greenPale,
                    color: amount === String(amt) ? SBER.white : SBER.green
                  }}
                >
                  {amt.toLocaleString()}
                </motion.button>
              ))}
            </div>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`${t ? t('topup.min') : 'от'} ${selectedProvider.min_amount} - ${selectedProvider.max_amount}`}
              className="w-full text-[16px] bg-transparent outline-none"
              style={{ color: SBER.dark }}
              data-testid="amount-input"
            />
          </SberCard>
        )}
        
        <SberCard className="p-4 mb-4" style={{ background: '#DBEAFE' }}>
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-[13px] text-blue-800">
              <p>Оплата будет произведена через eMoney.tools</p>
              <p className="mt-1">{t ? t('withdraw.processing') : 'Средства зачислятся в течение нескольких минут'}</p>
            </div>
          </div>
        </SberCard>
        
        <motion.button
          whileTap={tapAnimation}
          onClick={handlePayment}
          disabled={loading || !account}
          className="w-full py-4 rounded-xl font-semibold text-[16px] text-white transition-all
            disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ background: SBER.green, boxShadow: '0 4px 16px rgba(33, 160, 56, 0.3)' }}
          data-testid="payment-button"
        >
          {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : (
            <>
              <Zap className="w-5 h-5" />
              {t ? t('actions.confirm') : 'Оплатить'}
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default PaymentsPage;
